import "focus-visible";
import "normalize.css";
import React from "react";
import { render } from "react-dom";
import { BrowserRouter, Route, Switch } from "react-router-dom";
import smoothscroll from "smoothscroll-polyfill";
import { ThemeProvider } from "styled-components";
import { ApolloProvider } from "../Apollo";
import Analytics from "../components/common/Analytics";
import Navigation from "../components/common/Navigation";
import NotFound from "../components/common/NotFound";
import ScrollToTop from "../components/common/ScrollToTop";
import useQueryParams from "../components/common/useQueryParams";
import HomebeatEdit from "../components/homebeat/HomebeatEdit";
import HomebeatNew from "../components/homebeat/HomebeatNew";
import Homebeats from "../components/homebeat/Homebeats";
import HomebeatsImport from "../components/homebeat/HomebeatsImport";
import HomebeatViewer from "../components/homebeat/viewer/HomebeatViewer";
import Live from "../components/live/Live";
import ReportAveragePricePerSqftChart from "../components/report/ReportAveragePricePerSqftChart";
import ReportTimeToSellChart from "../components/report/ReportTimeToSellChart";
import Settings from "../components/settings/Settings";
import SettingsLeadGeneration from "../components/settings/SettingsLeadGeneration";
import GlobalStyle from "../GlobalStyle";
import { SessionProvider } from "../Session";
import theme from "../theme";

smoothscroll.polyfill();

function AppWithNavigation() {
  return (
    <>
      <Analytics />
      <Navigation />
      <Route
        exact
        path={[
          "/settings",
          "/settings/contact-info",
          "/settings/password",
          "/settings/mls-credentials",
          "/settings/custom-pages",
          "/settings/integrations",
          "/settings/billing",
          "/settings/billing/payments"
        ]}>
        <Settings />
      </Route>
      <Route path="/settings/lead-generation">
        <SettingsLeadGeneration />
      </Route>
    </>
  );
}

// * Notes: The analytics code lives inside of the apps in this category
// * This function deals with basically anything not in the admin app
// * so we must include the analytics code inside of each mini app
// * as they are dynamically loaded.
// * Also there is a bug in react helmet with updating the document title.
// * which will skew our data in analytics if we didn't wait for the title to update.
// * https://github.com/nfl/react-helmet/issues/189
function AppWithoutNavigation() {
  return (
    <>
      <Route path="/live/:guid">
        <Live />
      </Route>
      <Route path="/homebeats" exact>
        <Homebeats />
      </Route>
      <Route path="/homebeats/new">
        <HomebeatNew />
      </Route>
      <Route path="/homebeats/:id/edit">
        <HomebeatEdit />
      </Route>
      <Route path="/homebeats/import">
        <HomebeatsImport />
      </Route>
      <Route path="/homebeat/:guid">
        <HomebeatViewer />
      </Route>
    </>
  );
}

function RenderGlobalStyle() {
  const queryString = useQueryParams();
  return <GlobalStyle bg={queryString.get("bg")} />;
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <SessionProvider>
        <ApolloProvider>
          <BrowserRouter>
            <RenderGlobalStyle />
            <ScrollToTop />
            <Switch>
              <Route
                path={[
                  "/live/:guid",
                  "/homebeats",
                  "/homebeats/new",
                  "/homebeats/:id/edit",
                  "/homebeats/import",
                  "/homebeat/:guid"
                ]}>
                <AppWithoutNavigation />
              </Route>
              <Route
                exact
                path={[
                  "/settings",
                  "/settings/contact-info",
                  "/settings/password",
                  "/settings/mls-credentials",
                  "/settings/custom-pages",
                  "/settings/integrations",
                  "/settings/billing",
                  "/settings/billing/payments",
                  "/settings/lead-generation"
                ]}>
                <AppWithNavigation />
              </Route>
              <Route path="/reports/:reportId/charts/time_to_sell">
                <ReportTimeToSellChart />
              </Route>
              <Route path="/reports/:reportId/charts/average_price_per_sqft">
                <ReportAveragePricePerSqftChart />
              </Route>
              <Route>
                <div
                  style={{
                    width: "100vw",
                    height: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}>
                  <NotFound useNativeLink />
                </div>
              </Route>
            </Switch>
          </BrowserRouter>
        </ApolloProvider>
      </SessionProvider>
    </ThemeProvider>
  );
}

render(<App />, document.getElementById("root"));
